import React, { useState } from 'react';
import { Client } from '../../data/clients';
import { MapPin, Phone, Mail, Edit, Trash2, Clock } from 'lucide-react';
interface ClientsTableProps {
  clients: Client[];
  onEdit: (client: Client) => void;
  onDelete: (clientId: string) => void;
}
export function ClientsTable({
  clients,
  onEdit,
  onDelete
}: ClientsTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) || client.address.city.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = filterPriority === 'all' || client.priority === filterPriority;
    return matchesSearch && matchesPriority;
  });
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
    }
    return `${mins}min`;
  };
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200" data-id="element-141">
      {/* Filters */}
      <div className="p-4 border-b border-gray-200" data-id="element-142">
        <div className="flex items-center space-x-4" data-id="element-143">
          <input type="text" placeholder="Rechercher un client..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-144" />
          <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-145">
            <option value="all" data-id="element-146">Toutes priorités</option>
            <option value="high" data-id="element-147">Haute</option>
            <option value="medium" data-id="element-148">Moyenne</option>
            <option value="low" data-id="element-149">Basse</option>
          </select>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto" data-id="element-150">
        <table className="w-full" data-id="element-151">
          <thead className="bg-gray-50 border-b border-gray-200" data-id="element-152">
            <tr data-id="element-153">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-154">
                Client
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-155">
                Adresse
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-156">
                Durée déneigement
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-157">
                Priorité
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-158">
                Contact
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-159">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-160">
            {filteredClients.map(client => <tr key={client.id} className="hover:bg-gray-50 transition-colors" data-id="element-161">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-162">
                  <div data-id="element-163">
                    <div className="text-sm font-semibold text-gray-900" data-id="element-164">
                      {client.name}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-165">{client.id}</div>
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-166">
                  <div className="flex items-start space-x-2" data-id="element-167">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-168" />
                    <div className="text-sm text-gray-900" data-id="element-169">
                      <div data-id="element-170">{client.address.street}</div>
                      <div className="text-gray-500" data-id="element-171">
                        {client.address.city}, {client.address.postalCode}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-172">
                  <div className="flex items-center space-x-2" data-id="element-173">
                    <Clock className="w-4 h-4 text-blue-600" data-id="element-174" />
                    <span className="text-sm font-semibold text-gray-900" data-id="element-175">
                      {formatDuration(client.snowRemovalDuration)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-176">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${client.priority === 'high' ? 'bg-red-100 text-red-700' : client.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-177">
                    {client.priority === 'high' ? 'Haute' : client.priority === 'medium' ? 'Moyenne' : 'Basse'}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-178">
                  <div className="text-sm" data-id="element-179">
                    <div className="text-gray-900" data-id="element-180">{client.contact.name}</div>
                    <div className="flex items-center space-x-3 mt-1" data-id="element-181">
                      <div className="flex items-center space-x-1 text-gray-500" data-id="element-182">
                        <Phone className="w-3 h-3" data-id="element-183" />
                        <span className="text-xs" data-id="element-184">{client.contact.phone}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-gray-500" data-id="element-185">
                        <Mail className="w-3 h-3" data-id="element-186" />
                        <span className="text-xs" data-id="element-187">{client.contact.email}</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-188">
                  <div className="flex items-center justify-end space-x-2" data-id="element-189">
                    <button onClick={() => onEdit(client)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-190">
                      <Edit className="w-4 h-4" data-id="element-191" />
                    </button>
                    <button onClick={() => onDelete(client.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-192">
                      <Trash2 className="w-4 h-4" data-id="element-193" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {filteredClients.length === 0 && <div className="text-center py-12" data-id="element-194">
          <p className="text-gray-500" data-id="element-195">Aucun client trouvé</p>
        </div>}
    </div>;
}